import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Avatar,
  CircularProgress,
} from "@mui/material";
import { motion } from "framer-motion";
import {
  FaUserGraduate,
  FaBook,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
  FaPhone,
  FaSignOutAlt,
  FaIdCard,
  FaGraduationCap,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { firestore } from "../Firebase";
import Bg from "../Images/about.jpg";

const MemberDashboard = () => {
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const darkGlassStyle = {
    background: "rgba(0, 0, 36, 0.85)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(0, 217, 255, 0.2)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
  };

  useEffect(() => {
    const checkMember = async () => {
      const storedMember = localStorage.getItem("member");
      if (!storedMember) {
        navigate("/Client/login");
        return;
      }

      const memberData = JSON.parse(storedMember);

      // Fetch latest data from Firestore
      try {
        const doc = await firestore.collection("registrations").doc(memberData.id).get();
        if (doc.exists) {
          const latestData = { id: doc.id, ...doc.data() };
          setMember(latestData);
          // Update localStorage with latest status
          localStorage.setItem("member", JSON.stringify(latestData));
        } else {
          localStorage.removeItem("member");
          navigate("/Client/login");
        }
      } catch (error) {
        console.error("Error fetching member:", error);
        setMember(memberData);
      } finally {
        setLoading(false);
      }
    };

    checkMember();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("member");
    navigate("/Client/login");
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case "approved":
        return {
          color: "#22c55e",
          bgColor: "rgba(34, 197, 94, 0.15)",
          icon: <FaCheckCircle />,
          label: "Approved",
          message: "Your membership is active. You have full access to all resources.",
        };
      case "rejected":
        return {
          color: "#ef4444",
          bgColor: "rgba(239, 68, 68, 0.15)",
          icon: <FaTimesCircle />,
          label: "Rejected",
          message: "Your application was not approved. Please contact the Treasurer for more information.",
        };
      default:
        return {
          color: "#f97316",
          bgColor: "rgba(249, 115, 22, 0.15)",
          icon: <FaClock />,
          label: "Pending Payment",
          message: "Please pay K30 to the Treasurer General to activate your membership.",
        };
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: `linear-gradient(135deg, rgba(0, 0, 36, 0.95) 0%, rgba(10, 25, 47, 0.9) 50%, rgba(0, 0, 36, 0.95) 100%), url(${Bg})`,
          backgroundSize: "cover",
        }}
      >
        <CircularProgress sx={{ color: "#00d9ff" }} size={60} />
      </Box>
    );
  }

  const statusConfig = getStatusConfig(member?.status);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, rgba(0, 0, 36, 0.95) 0%, rgba(10, 25, 47, 0.9) 50%, rgba(0, 0, 36, 0.95) 100%), url(${Bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
        color: "white",
        pt: { xs: 12, md: 14 },
        pb: 8,
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4, flexWrap: "wrap", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar
                sx={{
                  width: 70,
                  height: 70,
                  background: "linear-gradient(135deg, #00d9ff, #00b4d8)",
                  fontSize: "1.8rem",
                  fontWeight: 700,
                }}
              >
                {member?.fullName?.charAt(0).toUpperCase()}
              </Avatar>
              <Box>
                <Typography variant="h4" fontWeight={800} sx={{ color: 'white' }}>
                  Welcome, {member?.fullName?.split(" ")[0]}
                </Typography>
                <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.7)" }}>
                  Member Dashboard
                </Typography>
              </Box>
            </Box>
            <Button
              variant="outlined"
              onClick={handleLogout}
              startIcon={<FaSignOutAlt />}
              sx={{
                borderRadius: "12px",
                borderColor: "rgba(239, 68, 68, 0.5)",
                color: "#ef4444",
                "&:hover": {
                  borderColor: "#ef4444",
                  background: "rgba(239, 68, 68, 0.1)",
                },
              }}
            >
              Logout
            </Button>
          </Box>
        </motion.div>

        {/* Status Card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <Card
            sx={{
              borderRadius: "20px",
              background: statusConfig.bgColor,
              border: `1px solid ${statusConfig.color}50`,
              mb: 4,
              overflow: "hidden",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                <Box
                  sx={{
                    width: 50,
                    height: 50,
                    borderRadius: "12px",
                    background: statusConfig.color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontSize: "1.5rem",
                  }}
                >
                  {statusConfig.icon}
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={700} sx={{ color: "white" }}>
                    Membership Status
                  </Typography>
                  <Chip
                    label={statusConfig.label}
                    sx={{
                      background: statusConfig.color,
                      color: "white",
                      fontWeight: 600,
                      mt: 0.5,
                    }}
                  />
                </Box>
              </Box>
              <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.9)" }}>
                {statusConfig.message}
              </Typography>
              {member?.status !== "approved" && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    borderRadius: "12px",
                    background: "rgba(0, 0, 36, 0.5)",
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <FaPhone color="#00d9ff" />
                  <Typography variant="body1" sx={{ color: "white" }}>
                    Contact Treasurer: <strong style={{ color: "#00d9ff" }}>+260972451530</strong>
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Member Info Cards */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Card sx={{ borderRadius: "20px", ...darkGlassStyle, height: "100%" }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                    <Box
                      sx={{
                        width: 50,
                        height: 50,
                        borderRadius: "12px",
                        background: "linear-gradient(135deg, #00d9ff, #00b4d8)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <FaIdCard size={24} color="white" />
                    </Box>
                    <Typography variant="h6" fontWeight={700} sx={{ color: 'white' }}>
                      Member Information
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography sx={{ color: "rgba(255,255,255,0.6)" }}>Full Name</Typography>
                      <Typography fontWeight={600}>{member?.fullName}</Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography sx={{ color: "rgba(255,255,255,0.6)" }}>Student Number</Typography>
                      <Typography fontWeight={600} sx={{ color: "#00d9ff" }}>{member?.studentNumber}</Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography sx={{ color: "rgba(255,255,255,0.6)" }}>Phone</Typography>
                      <Typography fontWeight={600}>{member?.phoneNumber}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <Card sx={{ borderRadius: "20px", ...darkGlassStyle, height: "100%" }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                    <Box
                      sx={{
                        width: 50,
                        height: 50,
                        borderRadius: "12px",
                        background: "linear-gradient(135deg, #22c55e, #16a34a)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <FaGraduationCap size={24} color="white" />
                    </Box>
                    <Typography variant="h6" fontWeight={700} sx={{ color: 'white' }}>
                      Academic Details
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography sx={{ color: "rgba(255,255,255,0.6)" }}>Program</Typography>
                      <Typography fontWeight={600}>{member?.program}</Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography sx={{ color: "rgba(255,255,255,0.6)" }}>Year of Study</Typography>
                      <Typography fontWeight={600}>{member?.yearOfStudy}</Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography sx={{ color: "rgba(255,255,255,0.6)" }}>Member Since</Typography>
                      <Typography fontWeight={600}>
                        {member?.createdAt?.toDate?.()?.toLocaleDateString() || "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>

        {/* Resources Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <Card sx={{ borderRadius: "20px", ...darkGlassStyle, mt: 4 }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                <Box
                  sx={{
                    width: 50,
                    height: 50,
                    borderRadius: "12px",
                    background: "linear-gradient(135deg, #3b82f6, #2563eb)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaBook size={24} color="white" />
                </Box>
                <Typography variant="h6" fontWeight={700} sx={{ color: 'white' }}>
                  Member Resources
                </Typography>
              </Box>

              {member?.status === "approved" ? (
                <Box>
                  <Typography sx={{ color: "rgba(255,255,255,0.8)", mb: 3 }}>
                    Access exclusive study materials, past papers, and learning resources.
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => navigate("/Client/resources")}
                    sx={{
                      borderRadius: "12px",
                      px: 4,
                      py: 1.5,
                      background: "linear-gradient(135deg, #3b82f6, #2563eb)",
                      fontWeight: 600,
                      "&:hover": {
                        background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
                      },
                    }}
                  >
                    Access Resources
                  </Button>
                </Box>
              ) : (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 4,
                    background: "rgba(0, 0, 0, 0.2)",
                    borderRadius: "15px",
                  }}
                >
                  <FaBook size={50} color="rgba(255,255,255,0.3)" />
                  <Typography variant="h6" sx={{ color: "rgba(255,255,255,0.5)", mt: 2 }}>
                    Resources Locked
                  </Typography>
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.4)", mt: 1 }}>
                    Complete your K30 payment to unlock access to member resources
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </Container>
    </Box>
  );
};

export default MemberDashboard;
